import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Mail, Calendar, Save, Loader2, Bell, BellOff, Send, Trash2, LogOut, TrendingUp, MessageCircle, Inbox, Camera, Package } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '@/lib/api';
import { resizeImage } from '@/lib/imageResize';
import ShareAppButton from '@/components/ShareAppButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  isPushSupported,
  getSubscriptionState,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from '@/lib/push';
import { isNative, isIOS } from '@/lib/platform';
import {
  initIOSPushNotifications,
  teardownIOSPushNotifications,
  readIOSPushPermission,
  readBuildInfo,
} from '@/lib/pushNotificationsIOS';
import { Switch } from '@/components/ui/switch';

const API = API_BASE;

/**
 * Human-readable label for the current registration stage. Maps every
 * event name emitted by `pushNotificationsIOS.js` to a short verb
 * phrase so the UI never has to fall back to a generic "checking…"
 * placeholder. The label attributes stalls to a specific phase, which
 * is what makes the diagnostics panel actually useful when triaging
 * a stuck flow (listener attach vs. checkPermissions vs. register etc).
 */
function humanStage(latestStage, iosSteps, iosRegistering) {
  if (iosSteps?.registrationError) {
    return iosSteps.registrationErrorStage
      ? `failed at ${iosSteps.registrationErrorStage}`
      : 'failed';
  }
  switch (latestStage) {
    case 'native-check':                       return 'checking platform…';
    case 'listeners-started':                  return 'attaching listeners…';
    case 'listener-attached-registration':     return 'attaching listeners… (1/4)';
    case 'listener-attached-registrationError':return 'attaching listeners… (2/4)';
    case 'listener-attached-received':         return 'attaching listeners… (3/4)';
    case 'listener-attached-action':           return 'attaching listeners… (4/4)';
    case 'listeners-complete':                 return 'listeners ready';
    case 'check-permissions-started':          return 'checking permission…';
    case 'check-permissions-resolved':
    case 'check-permissions':                  return iosSteps?.permission ? `permission: ${iosSteps.permission}` : 'permission resolved';
    case 'request-permissions-started':        return 'requesting permission…';
    case 'request-permissions-resolved':
    case 'request-permissions':                return iosSteps?.permission ? `permission: ${iosSteps.permission}` : 'permission requested';
    case 'denied':                             return 'permission denied';
    case 'register-started':
    case 'register-called':                    return 'registering with APNs…';
    case 'register-resolved':                  return 'register() resolved — awaiting APNs token';
    case 'token-wait-started':                 return 'waiting for APNs token…';
    case 'token-received':                     return 'APNs token received — saving…';
    case 'token-saved':
      return iosSteps?.tokenSaved ? 'token saved' : 'token save failed';
    case 'done':                               return 'success';
    default:
      return iosRegistering ? 'working…' : (latestStage || 'idle');
  }
}


export default function ProfilePage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [pushState, setPushState] = useState({ supported: false, permission: 'default', subscribed: false });
  const [pushBusy, setPushBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [iosDiag, setIosDiag] = useState(null);
  const [iosBuild, setIosBuild] = useState({
    version: null, build: null,
    commitSha: null, bundleId: null,
    productionSource: null, productionBuiltAt: null,
  });

  // Toggle-driven registration state — every step of the native flow so
  // the UI can render exactly where the process is up to.
  const [iosToggle, setIosToggle] = useState(false);
  const [iosRegistering, setIosRegistering] = useState(false);
  const [iosSteps, setIosSteps] = useState({
    nativePlatform: null,
    permission: 'unknown',           // OS permission — starts unknown, never
                                     // auto-queried on mount (that was the
                                     // source of the "checking…" hang).
    registerCalled: false,
    tokenLast8: null,
    tokenSaved: null,                // true | false | null (not attempted)
    tokenSaveError: null,
    registrationError: null,
    registrationErrorStage: null,    // 'native-check' | 'plugin' | 'listener' |
                                     // 'request-timeout' | 'request-denied' |
                                     // 'register-timeout' | 'registration-error' |
                                     // 'token-timeout' | 'token-save-error' |
                                     // 'unexpected'
    deniedMessage: null,
    latestStage: null,               // most recent step name (raw)
    latestTimestamp: null,           // ms epoch of the last step arrival —
                                     // helpful when triaging a stall
  });

  const handleStep = (evt) => {
    setIosSteps((prev) => {
      const next = { ...prev };
      // Every step updates the "latest" markers so the diagnostics can
      // render exactly which phase is currently in-flight (or where
      // the flow stopped). This is the ONLY reliable way for the user
      // to know whether the stall is inside addListener attach,
      // checkPermissions, requestPermissions, register, or token-save.
      if (evt.step) {
        next.latestStage = evt.step;
        next.latestTimestamp = Date.now();
      }
      switch (evt.step) {
        case 'native-check':      next.nativePlatform = evt.ok; break;
        case 'check-permissions': next.permission = evt.permission; break;
        case 'check-permissions-resolved': next.permission = evt.permission; break;
        case 'request-permissions': next.permission = evt.permission; break;
        case 'request-granted':   next.permission = evt.permission || 'granted'; break;
        case 'request-denied':    next.permission = evt.permission || 'denied'; break;
        case 'register-called':   next.registerCalled = true; break;
        case 'register-started':  next.registerCalled = true; break;
        case 'calling-register':  next.registerCalled = true; break;
        case 'token-received':    next.tokenLast8 = evt.tokenLast8; break;
        case 'token-saved':
          next.tokenSaved = evt.ok;
          next.tokenSaveError = evt.error;
          next.tokenLast8 = evt.tokenLast8 || next.tokenLast8;
          break;
        case 'success':
          next.tokenSaved = true;
          next.tokenSaveError = null;
          next.tokenLast8 = evt.tokenLast8 || next.tokenLast8;
          break;
        case 'token-save-error':
          next.tokenSaved = false;
          next.tokenSaveError = evt.error;
          next.tokenLast8 = evt.tokenLast8 || next.tokenLast8;
          break;
        case 'flow-watchdog-timeout':
          next.registrationError = `flow-watchdog-timeout (last: ${evt.lastCompletedOp || 'unknown'})`;
          next.registrationErrorStage = 'flow-watchdog-timeout';
          if (!next.permission || next.permission === 'unknown') {
            next.permission = 'failed';
          }
          break;
        case 'registration-error':
          next.registrationError = evt.error;
          next.registrationErrorStage = evt.stage || null;
          // A registration failure means the permission label should
          // NOT keep saying "checking…". Flip it to `failed` so the
          // UI clearly transitions out of the loading state, then rely
          // on the error line below to surface the exact reason.
          if (!next.permission || next.permission === 'unknown') {
            next.permission = 'failed';
          }
          break;
        case 'denied':            next.deniedMessage = evt.message; break;
        default: break;
      }
      return next;
    });
  };

  // Reset the diagnostics panel to a fresh run before either the toggle
  // or the manual "Request permission" button kicks off a new attempt.
  const resetIosSteps = () => {
    setIosSteps({
      nativePlatform: null, permission: 'unknown', registerCalled: false,
      tokenLast8: null, tokenSaved: null, tokenSaveError: null,
      registrationError: null, registrationErrorStage: null,
      deniedMessage: null,
      latestStage: null, latestTimestamp: null,
    });
  };

  // Fire-and-forget diagnostics refresh — MUST NEVER be awaited inside
  // the critical registration path, because if the backend is slow to
  // respond the toggle+button would remain locked past the point where
  // the user already saw the result. Kicked off via `setTimeout(…, 0)`
  // to guarantee it lands on the next macrotask, after the current
  // handler has released `iosRegistering`.
  const scheduleDiagnosticsRefresh = () => {
    setTimeout(() => { refreshIosDiagnostics().catch(() => {}); }, 0);
  };

  // Shared registration path used by BOTH the toggle-on and the manual
  // debug button. The toggle only flips ON if the full chain
  // (requestPermissions → register → APNs token → backend save) succeeds.
  //
  // Per-phase 8s/8s/10s timeouts are enforced inside
  // `initIOSPushNotifications`. The library returns a specific stage
  // for whichever phase failed — the UI just relays it.
  const runIosRegistration = async () => {
    resetIosSteps();
    let result;
    try {
      result = await initIOSPushNotifications(handleStep);
    } catch (err) {
      const msg = err?.message || String(err);
      handleStep({ step: 'registration-error', error: msg, stage: 'unexpected' });
      setIosToggle(false);
      toast.error(`Push registration threw: ${msg}`);
      scheduleDiagnosticsRefresh();
      return { ok: false, stage: 'unexpected', error: msg };
    }

    if (result.ok) {
      setIosToggle(true);
      toast.success('Notifications enabled on this device');
    } else {
      // Every failure path reverts the toggle to OFF — the user asked
      // for this behaviour explicitly. The exact stage is already in
      // the diagnostics panel via handleStep.
      setIosToggle(false);
      // Ensure the failure ALSO surfaces in the diagnostics panel
      // (some code paths return without going through emit).
      if (result.stage && !result.ok) {
        handleStep({
          step: 'registration-error',
          error: result.error || 'unknown',
          stage: result.stage,
        });
      }
      if (result.permission === 'denied' || result.stage === 'request-denied' || result.stage === 'denied') {
        toast.error(
          'Notifications are blocked. Open iPhone Settings → Kinsy → Notifications to allow, then try again.',
          { duration: 10000 },
        );
      } else if (result.stage === 'native-check') {
        toast.error('Push notifications are only available in the Kinsy iOS app.');
      } else if (result.stage === 'request-timeout') {
        toast.error(
          'The iOS permission dialog did not respond within 8 seconds. Force-quit Kinsy and reopen it, then try again.',
          { duration: 10000 },
        );
      } else if (result.stage === 'request-error') {
        toast.error(
          `iOS rejected the permission request: ${result.error || 'unknown'}`,
          { duration: 10000 },
        );
      } else if (result.stage === 'register-timeout') {
        toast.error('APNs register() did not respond within 8 seconds. Try again in a moment.', { duration: 8000 });
      } else if (result.stage === 'token-timeout') {
        toast.error('APNs did not return a device token within 10 seconds. Check your internet connection and retry.', { duration: 8000 });
      } else if (result.stage === 'token-save-error') {
        toast.error(`Backend rejected the APNs token: ${result.error}`, { duration: 8000 });
      } else if (result.stage === 'flow-watchdog-timeout') {
        toast.error(
          `Push flow watchdog fired at 12s (last: ${result.lastCompletedOp || 'unknown'}). Force-quit Kinsy and reopen to retry.`,
          { duration: 10000 },
        );
      } else {
        const label = result.stage ? ` (${result.stage})` : '';
        toast.error(
          result.error ? `Push registration failed${label}: ${result.error}` : `Push registration failed${label}`,
        );
      }
    }
    // Fire-and-forget — must NEVER be awaited inside the critical path.
    scheduleDiagnosticsRefresh();
    return result;
  };

  const handleIosToggle = async (checked) => {
    if (iosRegistering) return;
    setIosRegistering(true);
    try {
      if (checked) {
        await runIosRegistration();
      } else {
        try {
          await teardownIOSPushNotifications();
        } catch (err) {
          // Even a teardown failure must not leave the toggle stuck.
          toast.error(`Failed to detach: ${err?.message || err}`);
        }
        setIosToggle(false);
        toast.success('Notifications disabled on this device');
        scheduleDiagnosticsRefresh();
      }
    } catch (err) {
      // Defensive: any unexpected throw still surfaces + unlocks.
      handleStep({ step: 'registration-error', error: err?.message || String(err), stage: 'unexpected' });
      setIosToggle(false);
      toast.error(`Push toggle failed: ${err?.message || err}`);
    } finally {
      // `finally` is critical: any thrown or timed-out step must still
      // release the toggle so the user can retry.
      setIosRegistering(false);
    }
  };

  // Manual debug button — triggers the same registration chain as the
  // toggle without depending on toggle state. Used when the iOS
  // permission dialog was dismissed, or when we want to force-retry
  // after a previous timeout.
  const handleRequestIosPermission = async () => {
    if (iosRegistering) return;
    setIosRegistering(true);
    try {
      await runIosRegistration();
    } catch (err) {
      handleStep({ step: 'registration-error', error: err?.message || String(err), stage: 'unexpected' });
      toast.error(`Push registration failed: ${err?.message || err}`);
    } finally {
      setIosRegistering(false);
    }
  };

  const refreshIosDiagnostics = async () => {
    if (!(isNative() && isIOS())) return;
    try {
      // Server-side view: what tokens do we have persisted?
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/push/diagnostics`,
        { withCredentials: true, timeout: 8000 },
      );
      // Client-side view: current OS permission. Wrapped in a 5s
      // timeout inside readIOSPushPermission so it can never hang the
      // Profile page. On refresh (not on mount) it's safe to read.
      const permission = await readIOSPushPermission();
      const latest = Array.isArray(data.ios_tokens) && data.ios_tokens.length > 0 ? data.ios_tokens[0] : null;
      setIosDiag({
        permission,
        serverTokenCount: data.ios_tokens_count,
        tokenLast8: latest?.token_last8,
        env: data.apns_environment,
        configured: data.apns_configured,
        lastError: null,
      });
      // Sync toggle from server: only treat as ON when we have BOTH an
      // active APNs token AND OS-level permission is granted. Anything
      // else falls back to OFF so the user knows they need to re-run
      // the registration.
      if (data.ios_tokens_count > 0 && permission === 'granted') {
        setIosToggle(true);
      } else {
        setIosToggle(false);
      }
    } catch (err) {
      setIosDiag((prev) => ({ ...(prev || {}), lastError: err?.message || 'diagnostics fetch failed' }));
    }
  };

  useEffect(() => {
    // IMPORTANT: on mount we ONLY hit the backend for server-side
    // diagnostics — we do NOT call any native Capacitor method. Earlier
    // versions called PushNotifications.checkPermissions() here, which
    // could hang indefinitely on certain iOS states and left the toggle
    // stuck on "checking…". The permission is now read on-demand by
    // the "Refresh" button or during an explicit registration attempt.
    (async () => {
      if (!(isNative() && isIOS())) return;
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/push/diagnostics`,
          { withCredentials: true, timeout: 8000 },
        );
        const latest = Array.isArray(data.ios_tokens) && data.ios_tokens.length > 0 ? data.ios_tokens[0] : null;
        setIosDiag({
          permission: 'unknown',            // deliberately not queried on mount
          serverTokenCount: data.ios_tokens_count,
          tokenLast8: latest?.token_last8,
          env: data.apns_environment,
          configured: data.apns_configured,
          lastError: null,
        });
        // Reflect server truth in the toggle: if we already have a
        // token on file, tentatively show ON — the user can still
        // toggle off. We do NOT rely on OS permission here because we
        // intentionally skip that native call at mount time.
        if (data.ios_tokens_count > 0) {
          setIosToggle(true);
        } else {
          setIosToggle(false);
        }
      } catch (err) {
        setIosDiag({ permission: 'unknown', serverTokenCount: null, lastError: err?.message || 'diagnostics fetch failed' });
      }
      // Best-effort build info fetch — never throws, never blocks the
      // rest of the page. Populated once and rendered read-only in the
      // diagnostics panel so a user can confirm the installed IPA
      // matches the fixed commit.
      try {
        const info = await readBuildInfo();
        setIosBuild(info || {
          version: null, build: null,
          commitSha: null, bundleId: null,
          productionSource: null, productionBuiltAt: null,
        });
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const handleAvatarChange = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = '';
    if (!rawFile) return;
    if (!rawFile.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (rawFile.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8 MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const file = await resizeImage(rawFile);
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/profile/avatar`, form, { withCredentials: true });
      await refreshUser();
      toast.success('Profile photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!isPushSupported()) {
        setPushState({ supported: false, permission: 'default', subscribed: false });
        return;
      }
      const s = await getSubscriptionState();
      if (mounted) setPushState(s);
    })();
    return () => { mounted = false; };
  }, []);

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush();
      const s = await getSubscriptionState();
      setPushState(s);
      toast.success('Push notifications enabled!');
    } catch (err) {
      toast.error(err.message || 'Failed to enable notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      const s = await getSubscriptionState();
      setPushState(s);
      toast.success('Notifications disabled');
    } catch (err) {
      toast.error(err.message || 'Failed to disable notifications');
    } finally {
      setPushBusy(false);
    }
  };

  const handleTestPush = async () => {
    setPushBusy(true);
    try {
      await sendTestPush();
      toast.success('Test notification sent — check your device');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/account`, { withCredentials: true });
      toast.success('Account deleted. Goodbye!');
      // Clear auth state then redirect
      try { await logout(); } catch (_) {}
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/profile?name=${encodeURIComponent(name.trim())}`, {}, { withCredentials: true });
      await refreshUser();
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="profile-page">
      <Header />
      
      <main className="max-w-2xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold text-stone-900 mb-6">Profile</h1>

        {/* Profile Header */}
        <div className="card-soft mb-6" data-testid="profile-header">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar className="h-20 w-20 border-4 border-teal-100">
                {user?.avatar && (
                  <AvatarImage
                    src={`${API}/files/${user.avatar}`}
                    alt={user?.name}
                    className="object-cover"
                  />
                )}
                <AvatarFallback className="bg-teal-600 text-white text-2xl font-medium">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarChange}
                data-testid="avatar-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center shadow-md ring-2 ring-white transition disabled:opacity-60"
                title="Change profile photo"
                aria-label="Change profile photo"
                data-testid="avatar-upload-btn"
              >
                {uploadingAvatar ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
            </div>
            <div>
              <h2 className="text-xl font-bold text-stone-900">{user?.name}</h2>
              <p className="text-stone-500">{user?.email}</p>
              <span className="inline-flex items-center gap-1 text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-full mt-2">
                {user?.role === 'admin' ? 'Family Admin' : 'Member'}
              </span>
            </div>
          </div>
        </div>

        {/* Owner-only: Activity dashboard shortcut */}
        {user?.is_owner && (
          <button
            type="button"
            onClick={() => navigate('/admin/activity')}
            className="w-full mb-3 card-soft hover:bg-teal-50/40 transition flex items-center justify-between text-left"
            data-testid="owner-activity-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <div className="font-semibold text-stone-900">Activity dashboard</div>
                <div className="text-xs text-stone-500">Signups, subscriptions, print orders</div>
              </div>
            </div>
            <span className="text-teal-600 text-sm font-medium">Open →</span>
          </button>
        )}
        {user?.is_owner && (
          <button
            type="button"
            onClick={() => navigate('/admin/feedback')}
            className="w-full mb-3 card-soft hover:bg-teal-50/40 transition flex items-center justify-between text-left"
            data-testid="owner-feedback-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <Inbox className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <div className="font-semibold text-stone-900">Customer feedback</div>
                <div className="text-xs text-stone-500">Bugs, ideas, compliments — reply &amp; resolve</div>
              </div>
            </div>
            <span className="text-teal-600 text-sm font-medium">Open →</span>
          </button>
        )}
        {user?.is_owner && (
          <button
            type="button"
            onClick={() => navigate('/admin/analytics')}
            className="w-full mb-3 card-soft hover:bg-teal-50/40 transition flex items-center justify-between text-left"
            data-testid="owner-analytics-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <div className="font-semibold text-stone-900">Product analytics</div>
                <div className="text-xs text-stone-500">Funnel, activation, weekly active families</div>
              </div>
            </div>
            <span className="text-teal-600 text-sm font-medium">Open →</span>
          </button>
        )}
        {user?.is_owner && (
          <button
            type="button"
            onClick={() => navigate('/admin/push')}
            className="w-full mb-3 card-soft hover:bg-teal-50/40 transition flex items-center justify-between text-left"
            data-testid="owner-push-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                <Bell className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <div className="font-semibold text-stone-900">Push notification tester</div>
                <div className="text-xs text-stone-500">Send a test push to any user (iOS + web)</div>
              </div>
            </div>
            <span className="text-teal-600 text-sm font-medium">Open →</span>
          </button>
        )}

        {/* My orders — visible to every user */}
        <button
          type="button"
          onClick={() => navigate('/orders')}
          className="w-full mb-3 card-soft hover:bg-teal-50/40 transition flex items-center justify-between text-left"
          data-testid="my-orders-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <Package className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="font-semibold text-stone-900">Print orders</div>
              <div className="text-xs text-stone-500">Track your photo books &amp; prints</div>
            </div>
          </div>
          <span className="text-teal-600 text-sm font-medium">Open →</span>
        </button>

        {/* Feedback link — visible to every user */}
        <button
          type="button"
          onClick={() => navigate('/feedback')}
          className="w-full mb-6 card-soft hover:bg-teal-50/40 transition flex items-center justify-between text-left"
          data-testid="send-feedback-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="font-semibold text-stone-900">Send feedback</div>
              <div className="text-xs text-stone-500">Bugs, ideas, or just a hello — we read every one</div>
            </div>
          </div>
          <span className="text-teal-600 text-sm font-medium">Open →</span>
        </button>

        {/* Edit Profile Form */}
        <div className="card-soft" data-testid="profile-form">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Edit Profile</h3>
          
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-stone-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Display Name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                value={user?.email || ''}
                disabled
                className="rounded-xl border-stone-200 bg-stone-50"
              />
              <p className="text-xs text-stone-500">Email cannot be changed</p>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-700 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Member Since
              </Label>
              <Input
                value={user?.created_at ? formatDate(user.created_at) : ''}
                disabled
                className="rounded-xl border-stone-200 bg-stone-50"
              />
            </div>

            <Button
              type="submit"
              disabled={saving || name === user?.name}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </form>
        </div>

        {/* Share Kinsy */}
        <div className="card-soft mt-6" data-testid="share-app-card">
          <h3 className="text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
            <Send className="w-5 h-5 text-teal-600" /> Invite another family
          </h3>
          <p className="text-sm text-stone-500 mb-4">
            Know a family who'd love a private space of their own? Send them the Kinsy beta link — one text is all it takes.
          </p>
          <ShareAppButton
            className="w-full sm:w-auto rounded-full bg-teal-600 hover:bg-teal-700 text-white"
            label="Share Kinsy via text"
            testId="profile-share-app-button"
          />
        </div>

        {/* Push Notifications */}
        <div className="card-soft mt-6" data-testid="notifications-card">
          <h3 className="text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
            <Bell className="w-5 h-5 text-teal-600" /> Notifications
          </h3>
          <p className="text-sm text-stone-500 mb-4">
            Get instant alerts for new photos, posts, birthdays and "on this day" memories.
          </p>

          {/* Native iOS (TestFlight / App Store) — the toggle triggers the
              full APNs permission + registration flow. */}
          {isNative() && isIOS() && (
            <div className="space-y-3" data-testid="push-native-ios">
              <div className="flex items-center justify-between p-3 rounded-lg bg-teal-50 border border-teal-100">
                <div className="pr-3">
                  <div className="font-medium text-teal-900">Push notifications</div>
                  <div className="text-xs text-teal-800">
                    Turn on to allow Kinsy to send you notifications on this iPhone.
                  </div>
                </div>
                <Switch
                  checked={iosToggle}
                  onCheckedChange={handleIosToggle}
                  disabled={iosRegistering}
                  data-testid="ios-push-toggle"
                />
              </div>

              {/* Manual debug button — the spec explicitly requires this so
                  a user (or the admin) can force-retry the APNs
                  permission + register flow independent of the toggle
                  state. Useful when iOS silently dismisses the prompt or
                  a previous run timed out. */}
              <Button
                onClick={handleRequestIosPermission}
                disabled={iosRegistering}
                variant="outline"
                className="w-full rounded-full border-teal-200 text-teal-700 hover:bg-teal-50"
                data-testid="request-ios-permission-btn"
              >
                {iosRegistering ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Requesting…</>
                ) : (
                  <><Bell className="w-4 h-4 mr-2" /> Request iOS Notification Permission</>
                )}
              </Button>

              {/* Live registration diagnostics — the exact step-by-step
                  breadcrumb requested in the spec. Only rendered once a
                  registration attempt has started or completed.

                  The permission status now derives from the actual
                  latest step (see `humanStage()` below), not from a
                  generic "checking…" label — this is what makes the
                  stall attributable to a specific phase instead of an
                  opaque loading state. */}
              {(iosRegistering || iosSteps.nativePlatform !== null) && (
                <div className="text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded-lg p-3 space-y-1" data-testid="ios-registration-steps">
                  <div className="font-semibold text-stone-800">Registration diagnostics</div>
                  <div>Native platform: <span className="font-mono" data-testid="diag-native">{String(iosSteps.nativePlatform)}</span></div>
                  <div>
                    Latest stage:{' '}
                    <span className="font-mono" data-testid="diag-latest-stage">
                      {humanStage(iosSteps.latestStage, iosSteps, iosRegistering)}
                    </span>
                    {iosSteps.latestTimestamp && (
                      <span className="text-stone-500"> · {new Date(iosSteps.latestTimestamp).toLocaleTimeString()}</span>
                    )}
                  </div>
                  <div>Permission status: <span className="font-mono" data-testid="diag-permission">{iosSteps.permission && iosSteps.permission !== 'unknown' ? iosSteps.permission : (iosRegistering ? humanStage(iosSteps.latestStage, iosSteps, iosRegistering) : 'unknown')}</span></div>
                  <div>register() called: <span className="font-mono" data-testid="diag-register-called">{String(iosSteps.registerCalled)}</span></div>
                  <div>APNs token received: <span className="font-mono" data-testid="diag-token">{iosSteps.tokenLast8 ? `…${iosSteps.tokenLast8}` : '—'}</span></div>
                  <div>
                    Backend token save: <span className="font-mono" data-testid="diag-token-saved">
                      {iosSteps.tokenSaved === null ? '—' : (iosSteps.tokenSaved ? 'success' : 'failed')}
                    </span>
                    {iosSteps.tokenSaveError ? <span className="text-red-700"> · {iosSteps.tokenSaveError}</span> : null}
                  </div>
                  {iosSteps.registrationError && (
                    <div className="text-red-700" data-testid="diag-registration-error">
                      {iosSteps.registrationErrorStage
                        ? <>Failed at <span className="font-mono">{iosSteps.registrationErrorStage}</span>: </>
                        : 'Registration error: '}
                      <span className="font-mono">{iosSteps.registrationError}</span>
                    </div>
                  )}
                  {iosSteps.deniedMessage && (
                    <div className="text-amber-800" data-testid="diag-denied">{iosSteps.deniedMessage}</div>
                  )}
                </div>
              )}

              {/* Server-side view — what the backend has actually persisted
                  for this account. Kept below the live diagnostics because
                  the toggle-run panel is more actionable in the moment. */}
              <div className="text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded-lg p-3 space-y-1" data-testid="ios-diagnostics">
                <div className="font-semibold text-stone-800">Server state</div>
                <div>OS permission (on refresh): <span className="font-mono" data-testid="diag-os-permission">{iosDiag?.permission || 'unknown'}</span></div>
                <div>APNs tokens on server: <span className="font-mono" data-testid="diag-token-count">{iosDiag?.serverTokenCount ?? '…'}</span> {iosDiag?.tokenLast8 ? `(…${iosDiag.tokenLast8})` : ''}</div>
                <div>Server APNs environment: <span className="font-mono">{iosDiag?.env || '…'}</span></div>
                <div className="pt-1 mt-1 border-t border-stone-200">
                  App version: <span className="font-mono" data-testid="diag-app-version">{iosBuild?.version || 'unknown'}</span>
                  {' · '}Build: <span className="font-mono" data-testid="diag-app-build">{iosBuild?.build || 'unknown'}</span>
                  {' · '}Commit: <span className="font-mono" data-testid="diag-app-commit">{iosBuild?.commitSha || 'unavailable'}</span>
                  {iosBuild?.bundleId && (
                    <div className="text-stone-500" data-testid="diag-production-bundle">
                      Production bundle: <span className="font-mono">{iosBuild.bundleId}</span>
                      {iosBuild?.productionBuiltAt && ` · built ${new Date(iosBuild.productionBuiltAt).toLocaleString()}`}
                    </div>
                  )}
                </div>
                {iosDiag?.lastError && (
                  <div className="text-red-700">Last error: <span className="font-mono">{iosDiag.lastError}</span></div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshIosDiagnostics}
                  className="mt-1 h-7 px-2 text-teal-700 hover:bg-teal-50"
                  data-testid="diag-refresh-btn"
                >
                  Refresh
                </Button>
              </div>

              <Button
                onClick={handleTestPush}
                disabled={pushBusy || !iosToggle}
                variant="outline"
                className="w-full rounded-full border-teal-200 text-teal-700 hover:bg-teal-50"
                data-testid="test-push-btn-ios"
              >
                <Send className="w-4 h-4 mr-2" /> Send test notification to my devices
              </Button>
            </div>
          )}

          {/* Web push path — only shown when we're NOT running inside the
              iOS wrapper. The web-push banner would otherwise mislead
              TestFlight users into thinking push is broken. */}
          {!(isNative() && isIOS()) && !pushState.supported && (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="push-unsupported">
              Your browser doesn&apos;t support push notifications. Use the Kinsy iOS app for reliable delivery.
            </p>
          )}

          {!(isNative() && isIOS()) && pushState.supported && pushState.permission === 'denied' && (
            <p className="text-sm text-red-700 bg-red-50 rounded-lg p-3" data-testid="push-denied">
              Notifications are blocked in your browser settings. Re-enable them, then refresh this page.
            </p>
          )}

          {!(isNative() && isIOS()) && pushState.supported && pushState.permission !== 'denied' && !pushState.subscribed && (
            <Button
              onClick={handleEnablePush}
              disabled={pushBusy}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="enable-push-btn"
            >
              {pushBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
              Enable notifications
            </Button>
          )}

          {!(isNative() && isIOS()) && pushState.supported && pushState.subscribed && (
            <div className="flex flex-col sm:flex-row gap-2" data-testid="push-enabled-actions">
              <Button
                onClick={handleTestPush}
                disabled={pushBusy}
                variant="outline"
                className="flex-1 rounded-full border-teal-200 text-teal-700 hover:bg-teal-50"
                data-testid="test-push-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Send test
              </Button>
              <Button
                onClick={handleDisablePush}
                disabled={pushBusy}
                variant="outline"
                className="flex-1 rounded-full border-stone-200 text-stone-700 hover:bg-stone-50"
                data-testid="disable-push-btn"
              >
                <BellOff className="w-4 h-4 mr-2" /> Turn off
              </Button>
            </div>
          )}
        </div>

        {/* Danger zone */}
        <div className="card-soft mt-6 border-red-100" data-testid="danger-zone">
          <h3 className="text-lg font-semibold text-stone-900 mb-1 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" /> Delete account
          </h3>
          <p className="text-sm text-stone-500 mb-4">
            Permanently remove your account and personal content. This cannot be undone.
            {user?.role === 'admin' && (
              <span className="block mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                As family admin, you must remove all other members first — or your entire family will be deleted.
              </span>
            )}
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="w-full rounded-full border-red-200 text-red-600 hover:bg-red-50"
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete my account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-account-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete your Kinsy account?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes your profile, posts, comments, reactions and uploaded photos.
                  {user?.role === 'admin'
                    ? ' If you are the only member left, your family and all of its albums will also be deleted.'
                    : ' Your family keeps existing; only your personal content is removed.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogOut className="w-4 h-4 mr-2" />}
                  Delete forever
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </main>

      <MobileNav />
    </div>
  );
}
